import React, { ReactNode } from 'react'
import { Box, Accordion, AccordionDetails } from '@mui/material'
import { PartialAction, Embroidery } from 'src/types'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { BoxWrapper } from 'src/Components/Ui/BoxWrapper'
import { EmbroideryFormUpDown } from 'src/Components/Domain/EmbroideryFormUpDown'
import { EmbroideryAccordionSummary } from 'src/Components/Domain/EmbroideryAccordionSummary'
import { EmbroideryContentUs } from 'src/features/atoms/glove/us/Components/EmbroideryContent'
import { useLocale } from 'src/locales/useLocale'
import {
  checkUsAtomsCharacter,
  getUsAtomsCaution,
  getUsAtomsPositions,
  useUsAtomsEmbroideriesDispatch,
  getContentCaution
} from 'src/features/atoms/glove/us/logic/embroiderySetting'
import { SelectCardWithImage } from 'src/Components/Ui/SelectCardWithImage'
import { embroideryFlagGenerator, selectablePositionGenerator, emojiChecker } from 'src/logic'

type Props = {
  embroideries: Embroidery[]
  productNumber: string
  selectedIndex: number
  dispatch: React.Dispatch<PartialAction>
}

export const EmbroiderySetter: React.FC<Props> = ({ embroideries, productNumber, selectedIndex, dispatch }) => {
  const { t } = useLocale()
  const { handle } = useUsAtomsEmbroideriesDispatch(dispatch, embroideries, t)
  const positionObjects = getUsAtomsPositions(productNumber, t)
  const isYsr = false // このコンポーネントはATOMSのため、YSRの仕様は考慮しない
  const existEmoji = embroideries.map((e) => e.content).some(emojiChecker)
  return (
    <TabPanel selectedIndex={selectedIndex} index={2}>
      <EmbroideryFormUpDown {...{ embroideries, positionObjects, dispatch }} />
      {embroideries.map((embroidery, i) => {
        const e = embroideryFlagGenerator(embroidery)
        const c = checkUsAtomsCharacter(embroidery, productNumber, t)
        return (
          <Box key={`${embroidery.id}-${i}`} my={1}>
            <Accordion defaultExpanded>
              <EmbroideryAccordionSummary embroideryIndex={i} />
              <AccordionDetails style={{ flexWrap: 'wrap' }}>
                <BoxWrapper>
                  <SelectCard
                    summary={t.POSITION}
                    selectedLabel={embroidery.position?.label}
                    objects={selectablePositionGenerator(embroideries, positionObjects, [], isYsr, i)} // false部分はYSRでのみ仕様
                    index={i}
                    handleChange={handle.position}
                    disabled={c.existsContent}
                    description={getUsAtomsCaution(c.existsContent, 'position', t)}
                  />
                </BoxWrapper>
                <BoxWrapper>
                  <EmbroideryContentUs
                    content={embroidery.content}
                    contentMaxLength={c.contentMaxLength}
                    embroideryIndex={i}
                    disabled={e.isUnSelectedPosition}
                    caution={getContentCaution(existEmoji, c.contentCaution, e.isUnSelectedPosition, t)}
                    handleContent={handle.content}
                  />
                </BoxWrapper>
                <BoxWrapper mt={2}>
                  <SelectCardWithImage
                    summary={t.TYPE_FACE}
                    selectedLabel={embroidery.typeFace?.label}
                    objects={c.typeFaceObjects}
                    index={i}
                    handleChange={handle.typeFace}
                    description={getUsAtomsCaution(true, 'typeFace', t)}
                    disabled={!c.existsContent || c.isKoreaContents}
                    imageHeight={45}
                    imageWidth={150}
                  />
                </BoxWrapper>
                <BoxWrapper>
                  <SelectCard
                    summary={t.MAIN_COLOR}
                    selectedLabel={embroidery.color?.label}
                    selectedColor={embroidery.color?.color}
                    objects={t.EMBROIDERY_COLOR_OBJECTS}
                    disabled={e.isUnSelectedTypeFace}
                    index={i}
                    handleChange={handle.color}
                  />
                </BoxWrapper>
                <BoxWrapper>
                  <SelectCard
                    summary={t.SHADOW_COLOR}
                    selectedLabel={e.shadowColorLabel}
                    selectedColor={embroidery.shadowColor?.color}
                    objects={t.EMBROIDERY_SHADOW_EDGE_COLOR_OBJECTS}
                    disabled={e.disabledShadowColor}
                    defaultExpanded={false}
                    caution={getUsAtomsCaution(e.isUnSelectedMainColor, 'shadowColor', t)}
                    index={i}
                    handleChange={handle.shadowColor}
                  />
                </BoxWrapper>
                <BoxWrapper>
                  <SelectCard
                    summary={t.EDGE_COLOR}
                    selectedLabel={e.edgeColorLabel}
                    selectedColor={embroidery.edgeColor?.color}
                    objects={t.EMBROIDERY_SHADOW_EDGE_COLOR_OBJECTS}
                    disabled={e.disabledEdgeColor}
                    defaultExpanded={false}
                    caution={getUsAtomsCaution(e.isUnSelectedMainColor, 'edgeColor', t)}
                    index={i}
                    handleChange={handle.edgeColor}
                  />
                </BoxWrapper>
              </AccordionDetails>
            </Accordion>
          </Box>
        )
      })}
    </TabPanel>
  )
}
